import { Directive, ElementRef, HostListener, Input, OnInit, OnDestroy } from '@angular/core';

@Directive({
    selector: '[ckEditorEnhanced]',
    standalone: true
})
export class CkEditorEnhancedDirective implements OnInit, OnDestroy {
    @Input() ckEditorEnhanced: any;

    private isFullscreen: boolean = false;
    private editorElement: HTMLElement;
    private fullscreenButton: HTMLElement;
    private clipboardButtons: HTMLElement[] = [];
    private originalStyles: string = '';
    private preventingDefaultHandlers: boolean = false;

    constructor(private el: ElementRef) {}

    ngOnInit(): void {
        this.setupEnhancedFeatures();
    }

    ngOnDestroy(): void {
        if (this.isFullscreen) {
            this.exitFullscreen();
        }
        this.cleanup();
    }

    private cleanup(): void {
        if (this.fullscreenButton) {
            this.fullscreenButton.remove();
        }
        this.clipboardButtons.forEach(btn => btn.remove());
        this.clipboardButtons = [];

        // Remove keyboard event listeners
        this.removeKeyboardListeners();
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        // Only handle keyboard shortcuts when editor is focused
        const editableArea = this.editorElement?.querySelector('.ck-editor__editable') as HTMLElement;
        if (!editableArea || !editableArea.contains(event.target as Node)) {
            return;
        }

        const isCtrl = event.ctrlKey || event.metaKey;

        if (isCtrl) {
            switch (event.key.toLowerCase()) {
                case 'v':
                    if (event.shiftKey) {
                        // Ctrl+Shift+V - Paste as plain text
                        event.preventDefault();
                        event.stopPropagation();
                        this.executeClipboardAction('pastePlain');
                    } else {
                        // Ctrl+V - Regular paste (let default behavior, but enhance if needed)
                    }
                    break;
                case 'x':
                    // Ctrl+X - Cut
                    break;
                case 'c':
                    // Ctrl+C - Copy
                    break;
            }
        }
    }

    private addKeyboardListeners(): void {
        // Add keyboard event listener to the editor element
        if (this.editorElement) {
        }
    }

    private removeKeyboardListeners(): void {
        // Keyboard listeners are handled by @HostListener, no cleanup needed
    }

    private setupEnhancedFeatures(): void {

        // Use multiple aggressive timeouts to catch the editor whenever it loads
        const timeouts = [0, 100, 250, 500, 1000, 2000, 3000, 5000];
        timeouts.forEach(timeout => {
            setTimeout(() => {
                this.findAndEnhanceEditor();
            }, timeout);
        });

        // Also use mutation observer to detect when CKEditor is dynamically loaded
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;
                            if (element.querySelector('.ck-editor') || element.classList.contains('ck-editor')) {
                                setTimeout(() => this.findAndEnhanceEditor(), 100);
                            }
                        }
                    });
                }
            });
        });

        observer.observe(this.el.nativeElement, {
            childList: true,
            subtree: true
        });
    }

    private findAndEnhanceEditor(): void {
        // Try multiple selectors to find the editor
        const selectors = ['.ck-editor', 'ckeditor', '[data-editor]'];
        let editorFound = false;

        for (const selector of selectors) {
            this.editorElement = this.el.nativeElement.querySelector(selector);
            if (this.editorElement) {
                editorFound = true;
                break;
            }
        }

        // If no editor found in this element, check if this element IS the editor
        if (!editorFound && this.el.nativeElement.classList.contains('ck-editor')) {
            this.editorElement = this.el.nativeElement;
            editorFound = true;
        }

        if (!editorFound) {
            return;
        }

        if (this.editorElement.dataset['enhanced']) {
            return;
        }

        this.editorElement.dataset['enhanced'] = 'true';

        // Try to get the actual CKEditor instance from the Angular component
        const ckeditorComponent = this.el.nativeElement;
        let editorInstance = null;

        // Method 1: Try to get from the ckeditor Angular component
        if (ckeditorComponent && ckeditorComponent.__ngContext__) {
            const context = ckeditorComponent.__ngContext__;
            if (context && context.length > 0) {
                for (let i = 0; i < context.length; i++) {
                    const component = context[i];
                    if (component && component.editorInstance) {
                        editorInstance = component.editorInstance;
                        break;
                    }
                }
            }
        }

        // Method 2: Try to get from the DOM element
        if (!editorInstance) {
            const ckEditorDiv = this.editorElement;
            if (ckEditorDiv && (ckEditorDiv as any).ckeditorInstance) {
                editorInstance = (ckEditorDiv as any).ckeditorInstance;
            }
        }

        // Method 3: Try to find it in window/global scope
        if (!editorInstance && (window as any).CKEDITOR && (window as any).CKEDITOR.instances) {
            const instances = (window as any).CKEDITOR.instances;
            const keys = Object.keys(instances);
            if (keys.length > 0) {
                editorInstance = instances[keys[0]];
            }
        }


        if (editorInstance && (window as any).enhanceCKEditor) {
            (window as any).enhanceCKEditor(editorInstance);
        } else {
            // Fallback to manual toolbar enhancement
            // Find toolbar - try multiple approaches
            let toolbar = this.editorElement.querySelector('.ck-toolbar');
            if (!toolbar) {
                toolbar = this.editorElement.querySelector('.ck-toolbar__items');
            }
            if (!toolbar) {
                // Create toolbar if it doesn't exist
                const editorMain = this.editorElement.querySelector('.ck-editor__main');
                if (editorMain) {
                    toolbar = document.createElement('div') as HTMLElement;
                    toolbar.className = 'ck-toolbar';
                    (toolbar as HTMLElement).style.cssText = 'display: flex; align-items: center; padding: 8px; border-bottom: 1px solid #ddd; background: #f8f9fa;';
                    editorMain.insertBefore(toolbar, editorMain.firstChild);
                }
            }

            if (toolbar) {
                this.addClipboardButtons(toolbar as HTMLElement);
                this.enhanceResizeHandle();
                this.enforceEditorHeight();
                this.addKeyboardListeners();
            } else {
            }
        }
    }

    private addClipboardButtons(toolbar: HTMLElement): void {
        // Remove existing clipboard buttons
        this.clipboardButtons.forEach(btn => btn.remove());
        this.clipboardButtons = [];

        // Create clipboard button container
        const clipboardContainer = document.createElement('div');
        clipboardContainer.className = 'clipboard-buttons-container';
        clipboardContainer.style.cssText = `
            display: inline-flex !important;
            gap: 4px !important;
            margin: 0 8px !important;
            padding: 2px 8px !important;
            border-left: 2px solid #007bff !important;
            border-right: 2px solid #007bff !important;
            background: rgba(0, 123, 255, 0.05) !important;
            border-radius: 4px !important;
            align-items: center !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: relative !important;
            z-index: 1000 !important;
        `;

        // Define clipboard buttons
        const buttonConfigs = [
            { name: 'Cut', icon: '✂️', action: 'cut', color: '#000000', shortcut: 'Ctrl+X' },
            { name: 'Copy', icon: '📋', action: 'copy', color: '#000000', shortcut: 'Ctrl+C' },
            { name: 'Paste', icon: '📝', action: 'paste', color: '#000000', shortcut: 'Ctrl+V (or click and allow clipboard access)' },
            { name: 'Paste Plain', icon: '📄', action: 'pastePlain', color: '#000000', shortcut: 'Ctrl+Shift+V (or click and allow clipboard access)' }
        ];


        buttonConfigs.forEach((config, index) => {
            const button = this.createClipboardButton(config);
            clipboardContainer.appendChild(button);
            this.clipboardButtons.push(button);
        });

        // Insert clipboard container into toolbar
        const toolbarItems = toolbar.querySelector('.ck-toolbar__items');
        if (toolbarItems) {
            toolbarItems.appendChild(clipboardContainer);
        } else {
            toolbar.appendChild(clipboardContainer);
        }

        // Force visibility with additional CSS
        setTimeout(() => {
            clipboardContainer.style.display = 'inline-flex';
            clipboardContainer.style.visibility = 'visible';
            clipboardContainer.style.opacity = '1';
        }, 100);

    }

    private createClipboardButton(config: any): HTMLElement {
        const button = document.createElement('button') as HTMLButtonElement;
        button.type = 'button';
        button.className = 'ck-button clipboard-btn';
        button.title = `${config.name} (${config.shortcut})`;

        // Set button content
        button.innerHTML = `
            <span style="font-size: 14px; display: block;">${config.icon}</span>
        `;

        // Style button with forced visibility
        button.style.cssText = `
            min-width: 32px !important;
            height: 32px !important;
            border: 2px solid ${config.color} !important;
            border-radius: 4px !important;
            background: white !important;
            color: ${config.color} !important;
            cursor: pointer !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 2px !important;
            padding: 4px !important;
            transition: all 0.2s ease !important;
            font-weight: bold !important;
            font-size: 12px !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: relative !important;
            z-index: 1000 !important;
            outline: none !important;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
        `;

        // Add hover effects
        button.addEventListener('mouseenter', () => {
            button.style.backgroundColor = config.color + ' !important';
            button.style.color = 'white !important';
            button.style.transform = 'scale(1.05)';
            button.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3) !important';
        });

        button.addEventListener('mouseleave', () => {
            button.style.backgroundColor = 'white !important';
            button.style.color = config.color + ' !important';
            button.style.transform = 'scale(1)';
            button.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2) !important';
        });

        // Add click handler
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Immediate visual feedback
            button.style.backgroundColor = config.color + ' !important';
            button.style.color = 'white !important';
            button.style.transform = 'scale(0.95)';

            // Execute the action
            this.executeClipboardAction(config.action);

            // Reset visual feedback
            setTimeout(() => {
                button.style.backgroundColor = 'white !important';
                button.style.color = config.color + ' !important';
                button.style.transform = 'scale(1)';
            }, 150);
        });

        return button;
    }

    private executeClipboardAction(action: string): void {

        // Enhanced editable area detection
        let editableArea = this.findEditableArea();

        if (!editableArea) {
            this.showPasteMessage('Could not find editable area. Please click inside the editor first.');
            return;
        }

        try {
            // Focus the editor first to ensure it's active
            editableArea.focus();

            // Small delay to ensure focus takes effect
            setTimeout(() => {
                switch (action) {
                    case 'cut':
                        this.performSimpleCut(editableArea);
                        break;
                    case 'copy':
                        this.performSimpleCopy(editableArea);
                        break;
                    case 'paste':
                        this.performSimplePaste(editableArea, false);
                        break;
                    case 'pastePlain':
                        this.performSimplePaste(editableArea, true);
                        break;
                    default:
                }
            }, 100); // Increased delay to ensure focus
        } catch (error) {
        }
    }

    private findEditableArea(): HTMLElement | null {

        // Method 1: Look for CKEditor 5 editable area
        let editableArea = this.editorElement.querySelector('.ck-editor__editable') as HTMLElement;
        if (editableArea) {
            return editableArea;
        }

        // Method 2: Look for contenteditable element
        editableArea = this.editorElement.querySelector('[contenteditable="true"]') as HTMLElement;
        if (editableArea) {
            return editableArea;
        }

        // Method 3: Look for any contenteditable element
        editableArea = this.editorElement.querySelector('[contenteditable]') as HTMLElement;
        if (editableArea) {
            return editableArea;
        }

        // Method 4: Check if the element itself is editable
        if (this.editorElement.contentEditable === 'true') {
            return this.editorElement;
        }

        // Method 5: Try to find via CKEditor instance
        if (this.ckEditorEnhanced && this.ckEditorEnhanced.editing && this.ckEditorEnhanced.editing.view) {
            try {
                const view = this.ckEditorEnhanced.editing.view;
                const domRoot = view.getDomRoot();
                if (domRoot) {
                    return domRoot as HTMLElement;
                }
            } catch (e) {
            }
        }

        // Method 6: Look for any element with ck-content class
        editableArea = this.editorElement.querySelector('.ck-content') as HTMLElement;
        if (editableArea) {
            return editableArea;
        }

        // Method 7: Look in parent elements
        const parentEditor = this.editorElement.closest('.ck-editor');
        if (parentEditor) {
            editableArea = parentEditor.querySelector('.ck-editor__editable') as HTMLElement;
            if (editableArea) {
                return editableArea;
            }
        }

        return null;
    }

    private performSimpleCut(editableArea: HTMLElement): void {
        try {
            editableArea.focus();

            const success = document.execCommand('cut');
            if (success) {
            } else {
            }
        } catch (error) {
        }
    }

    private performSimpleCopy(editableArea: HTMLElement): void {
        try {
            editableArea.focus();

            const success = document.execCommand('copy');
            if (success) {
            } else {
            }
        } catch (error) {
        }
    }

    private performSimplePaste(editableArea: HTMLElement, plainTextOnly: boolean = false): void {
        try {

            editableArea.focus();

            // For paste operations, we need to use the Clipboard API since execCommand('paste') is restricted
            if (navigator.clipboard && navigator.clipboard.readText) {
                navigator.clipboard.readText().then(text => {

                    if (text && text.trim() !== '') {

                        // Use CKEditor command system to preserve undo/redo
                        if (this.executeViaCKEditorCommand(text, plainTextOnly)) {
                            setTimeout(() => {
                                this.showPasteMessage(`Pasted ${text.length} characters successfully!`);
                            }, 100);
                            return;
                        }

                        // Fallback to native browser paste simulation
                        if (this.simulateNativePaste(editableArea, text, plainTextOnly)) {
                            setTimeout(() => {
                                this.showPasteMessage(`Pasted ${text.length} characters successfully!`);
                            }, 100);
                            return;
                        }

                        // Final fallback to manual insertion
                        this.manualInsertText(editableArea, text, plainTextOnly);
                        setTimeout(() => {
                            this.showPasteMessage(`Pasted ${text.length} characters (manual mode)!`);
                        }, 200);

                    } else {
                        this.showPasteMessage('Clipboard is empty. Copy some text first.');
                    }
                }).catch(err => {
                    this.showPasteMessage('Clipboard access denied. Please use Ctrl+V to paste.');
                });
            } else {
                this.showPasteMessage('Clipboard API not available. Please use Ctrl+V to paste.');
            }
        } catch (error) {
            this.showPasteMessage('Paste operation failed. Please use Ctrl+V to paste.');
        }
    }

    private executeViaCKEditorCommand(text: string, plainTextOnly: boolean): boolean {
        try {

            if (!this.ckEditorEnhanced || !this.ckEditorEnhanced.execute) {
                return false;
            }

            // Try to use built-in CKEditor commands
            if (plainTextOnly) {
                // For plain text, clean first
                const cleanText = text
                    .replace(/<[^>]*>/g, '') // Remove HTML tags
                    .replace(/&[^;]+;/g, ' ') // Replace HTML entities with space
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim();

                // Try to execute insertText command
                this.ckEditorEnhanced.execute('insertText', { text: cleanText });
            } else {
                // For regular text, try insertText command
                this.ckEditorEnhanced.execute('insertText', { text: text });
            }

            return true;
        } catch (error) {
            return false;
        }
    }

    private simulateNativePaste(editableArea: HTMLElement, text: string, plainTextOnly: boolean): boolean {
        try {

            // Create a proper clipboard data object
            const clipboardData = new DataTransfer();

            if (plainTextOnly) {
                const cleanText = text
                    .replace(/<[^>]*>/g, '')
                    .replace(/&[^;]+;/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
                clipboardData.setData('text/plain', cleanText);
            } else {
                clipboardData.setData('text/plain', text);
                // Also set as HTML if it looks like HTML
                if (text.includes('<') && text.includes('>')) {
                    clipboardData.setData('text/html', text);
                }
            }

            // Create and dispatch paste event
            const pasteEvent = new ClipboardEvent('paste', {
                bubbles: true,
                cancelable: true,
                clipboardData: clipboardData
            });

            const eventResult = editableArea.dispatchEvent(pasteEvent);

            return eventResult;
        } catch (error) {
            return false;
        }
    }

    private manualInsertText(editableArea: HTMLElement, text: string, plainTextOnly: boolean): void {
        try {

            const textToInsert = plainTextOnly ?
                text.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').replace(/\s+/g, ' ').trim() :
                text;

            // Try document.execCommand first (for undo/redo compatibility)
            if (document.queryCommandSupported && document.queryCommandSupported('insertText')) {
                const result = document.execCommand('insertText', false, textToInsert);
                if (result) {
                    return;
                }
            }

            // Fallback to manual DOM manipulation
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode(textToInsert);
                range.insertNode(textNode);

                // Move cursor
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);

                // Dispatch events
                editableArea.dispatchEvent(new InputEvent('input', {
                    bubbles: true,
                    cancelable: false,
                    inputType: 'insertText',
                    data: textToInsert
                }));

            }
        } catch (error) {
        }
    }

    private tryPasteWithCKEditorAPI(text: string, plainTextOnly: boolean): boolean {
        try {

            if (!this.ckEditorEnhanced) {
                return false;
            }

            // Check if CKEditor has the required methods
            if (!this.ckEditorEnhanced.model || !this.ckEditorEnhanced.model.change) {
                return false;
            }

            const model = this.ckEditorEnhanced.model;

            model.change((writer: any) => {

                const selection = model.document.selection;

                // Get the position to insert at
                const insertPosition = selection.getFirstPosition();

                // Delete selected content if any
                if (!selection.isCollapsed) {
                    model.deleteContent(selection);
                }

                // Insert the text
                if (plainTextOnly) {
                    // For plain text, clean the text first
                    const cleanText = text
                        .replace(/<[^>]*>/g, '') // Remove HTML tags
                        .replace(/&[^;]+;/g, ' ') // Replace HTML entities with space
                        .replace(/\s+/g, ' ') // Normalize whitespace
                        .trim();

                    writer.insertText(cleanText, insertPosition);
                } else {
                    // For regular paste, insert as text
                    writer.insertText(text, insertPosition);
                }

            });

            // Force editor to update
            this.ckEditorEnhanced.editing.view.focus();

            return true;
        } catch (error) {
            return false;
        }
    }

    private insertText(editableArea: HTMLElement, text: string): void {
        try {
            // Try to use the Input event approach that CKEditor recognizes
            if (this.insertTextWithInputEvent(editableArea, text)) {
                return;
            }

            // Get current selection
            const selection = window.getSelection();

            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                // Delete any selected content
                range.deleteContents();

                // Insert as plain text to avoid conflicts
                const textNode = document.createTextNode(text);
                range.insertNode(textNode);

                // Move cursor to end of inserted text
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);

                // Trigger input event to notify CKEditor
                const inputEvent = new InputEvent('input', {
                    bubbles: true,
                    cancelable: true,
                    inputType: 'insertText',
                    data: text
                });
                editableArea.dispatchEvent(inputEvent);

                // Also dispatch mutation event for compatibility
                editableArea.dispatchEvent(new Event('input', { bubbles: true }));
                editableArea.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
                this.insertAtEnd(editableArea, text);
            }
        } catch (error) {
        }
    }

    private insertTextWithInputEvent(editableArea: HTMLElement, text: string): boolean {
        try {

            // Focus the editor
            editableArea.focus();

            // Create a more sophisticated input event
            const inputEvent = new InputEvent('beforeinput', {
                bubbles: true,
                cancelable: true,
                inputType: 'insertText',
                data: text
            });

            // Dispatch beforeinput event
            const beforeResult = editableArea.dispatchEvent(inputEvent);

            if (!beforeResult) {
                return false;
            }

            // Try using execCommand with insertText if available
            if (document.queryCommandSupported && document.queryCommandSupported('insertText')) {
                const result = document.execCommand('insertText', false, text);
                if (result) {

                    // Dispatch input event to notify CKEditor
                    const afterInputEvent = new InputEvent('input', {
                        bubbles: true,
                        cancelable: false,
                        inputType: 'insertText',
                        data: text
                    });
                    editableArea.dispatchEvent(afterInputEvent);

                    return true;
                }
            }

            return false;
        } catch (error) {
            return false;
        }
    }

    private insertAtEnd(editableArea: HTMLElement, text: string): void {
        try {
            const range = document.createRange();
            range.selectNodeContents(editableArea);
            range.collapse(false); // collapse to end
            const textNode = document.createTextNode(text);
            range.insertNode(textNode);

            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);

            // Notify CKEditor
            const inputEvent = new InputEvent('input', {
                bubbles: true,
                cancelable: false,
                inputType: 'insertText',
                data: text
            });
            editableArea.dispatchEvent(inputEvent);
            editableArea.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (error) {
        }
    }

    private showPasteMessage(message: string): void {
        // Create a temporary notification
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #ff9800;
            color: white;
            padding: 10px 15px;
            border-radius: 4px;
            z-index: 10000;
            font-size: 14px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        `;
        notification.textContent = message;
        document.body.appendChild(notification);

        // Remove after 3 seconds
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 3000);
    }

    private pasteAsPlainText(editableArea: HTMLElement): void {
        // This function is now replaced by performSimplePaste with plainTextOnly = true
        // Keep for backward compatibility
        this.performSimplePaste(editableArea, true);
    }

    private insertPlainText(editableArea: HTMLElement, text: string): void {
        try {
            // Clean the text - remove HTML and normalize whitespace
            const plainText = text
                .replace(/<[^>]*>/g, '') // Remove HTML tags
                .replace(/&[^;]+;/g, ' ') // Replace HTML entities with space
                .replace(/\s+/g, ' ') // Normalize whitespace
                .trim();



            // Get current selection
            const selection = window.getSelection();

            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                // Delete any selected content
                range.deleteContents();

                // Create and insert text node
                const textNode = document.createTextNode(plainText);
                range.insertNode(textNode);

                // Move cursor to end of inserted text
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);

                // Trigger events to notify CKEditor
                editableArea.dispatchEvent(new Event('input', { bubbles: true }));
                editableArea.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
                // Try to place cursor at end of editable area
                const range = document.createRange();
                range.selectNodeContents(editableArea);
                range.collapse(false); // collapse to end
                const textNode = document.createTextNode(plainText);
                range.insertNode(textNode);

                selection?.removeAllRanges();
                selection?.addRange(range);

                editableArea.dispatchEvent(new Event('input', { bubbles: true }));
                editableArea.dispatchEvent(new Event('change', { bubbles: true }));
            }
        } catch (error) {
        }
    }

    private addFullscreenButton(toolbar: HTMLElement): void {
        if (this.fullscreenButton) {
            this.fullscreenButton.remove();
        }

        this.fullscreenButton = document.createElement('button') as HTMLButtonElement;
        (this.fullscreenButton as HTMLButtonElement).type = 'button';
        this.fullscreenButton.innerHTML = '⛶';
        this.fullscreenButton.className = 'ck-button fullscreen-btn';
        this.fullscreenButton.title = 'Toggle Fullscreen (ESC to exit)';

        // Position absolutely in top-right corner
        this.fullscreenButton.style.cssText = `
            position: absolute !important;
            top: 4px !important;
            right: 4px !important;
            z-index: 1001 !important;
            width: 30px !important;
            height: 30px !important;
            border: 2px solid #007bff !important;
            border-radius: 4px !important;
            background: #f8f9fa !important;
            color: #007bff !important;
            cursor: pointer !important;
            font-size: 14px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            transition: all 0.2s ease !important;
            outline: none !important;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
        `;

        this.fullscreenButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleFullscreen();
        });

        this.fullscreenButton.addEventListener('mouseenter', () => {
            this.fullscreenButton.style.backgroundColor = '#007bff !important';
            this.fullscreenButton.style.color = 'white !important';
            this.fullscreenButton.style.transform = 'scale(1.1)';
        });

        this.fullscreenButton.addEventListener('mouseleave', () => {
            this.fullscreenButton.style.backgroundColor = '#f8f9fa !important';
            this.fullscreenButton.style.color = '#007bff !important';
            this.fullscreenButton.style.transform = 'scale(1)';
        });

        // Make toolbar relative to contain absolute button
        toolbar.style.position = 'relative';
        toolbar.appendChild(this.fullscreenButton);
    }

    private enhanceResizeHandle(): void {
        const editableElement = this.editorElement.querySelector('.ck-editor__editable') as HTMLElement;
        if (editableElement) {
            editableElement.style.minHeight = '500px';
            editableElement.style.resize = 'both';
            editableElement.style.overflow = 'auto';
        }
    }

    private enforceEditorHeight(): void {
        // Ensure CKEditor has proper height to prevent single-line collapse
        const editableElement = this.editorElement.querySelector('.ck-editor__editable') as HTMLElement;
        const contentElement = this.editorElement.querySelector('.ck-content') as HTMLElement;

        if (editableElement) {
            // Apply comprehensive height styling
            editableElement.style.minHeight = '300px';
            editableElement.style.maxHeight = '600px';
            editableElement.style.height = 'auto';
            editableElement.style.overflowY = 'auto';
            editableElement.style.resize = 'both'; // Enable diagonal resize handles
            editableElement.style.padding = '16px';
            editableElement.style.lineHeight = '1.6';
            editableElement.style.whiteSpace = 'normal';
            editableElement.style.wordWrap = 'break-word';
            editableElement.style.overflowWrap = 'break-word';

            // Ensure the editor doesn't become a single line
            editableElement.style.display = 'block';
            editableElement.style.boxSizing = 'border-box';
        }

        if (contentElement && contentElement !== editableElement) {
            // Apply same styles to content element if different
            contentElement.style.minHeight = '300px';
            contentElement.style.maxHeight = '600px';
            contentElement.style.height = 'auto';
            contentElement.style.overflowY = 'auto';
            contentElement.style.resize = 'both'; // Enable diagonal resize handles
            contentElement.style.padding = '16px';
            contentElement.style.lineHeight = '1.6';
            contentElement.style.whiteSpace = 'normal';
            contentElement.style.wordWrap = 'break-word';
            contentElement.style.overflowWrap = 'break-word';
        }

        // Also ensure the main editor container has proper height
        if (this.editorElement) {
            this.editorElement.style.minHeight = '350px';
            this.editorElement.style.position = 'relative'; // Required for resize functionality
        }

        // Force a layout recalculation to apply the changes
        setTimeout(() => {
            if (editableElement) {
                editableElement.style.display = 'none';
                editableElement.offsetHeight; // Trigger reflow
                editableElement.style.display = 'block';
            }
        }, 100);
    }

    private toggleFullscreen(): void {
        if (this.isFullscreen) {
            this.exitFullscreen();
        } else {
            this.enterFullscreen();
        }
    }

    private enterFullscreen(): void {
        this.isFullscreen = true;

        // Store original styles
        this.originalStyles = this.editorElement.style.cssText;

        // Apply fullscreen styles with proper containment
        this.editorElement.style.cssText = `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 10000 !important;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
        `;

        // Style the editable area for fullscreen
        const editableElement = this.editorElement.querySelector('.ck-editor__editable') as HTMLElement;
        if (editableElement) {
            editableElement.style.cssText = `
                flex: 1 !important;
                height: auto !important;
                max-height: none !important;
                min-height: auto !important;
                resize: none !important;
                margin: 0 !important;
                padding: 20px !important;
                border: none !important;
                border-radius: 0 !important;
                outline: none !important;
                overflow: auto !important;
                font-size: 14px !important;
                line-height: 1.6 !important;
            `;

            // Ensure the editable area maintains focus and prevents event bubbling
            editableElement.addEventListener('focus', this.preventFullscreenExit.bind(this));
            editableElement.addEventListener('blur', this.preventFullscreenExit.bind(this));
            editableElement.addEventListener('input', this.preventFullscreenExit.bind(this));
            editableElement.addEventListener('keydown', this.preventFullscreenExit.bind(this));
            editableElement.addEventListener('keyup', this.preventFullscreenExit.bind(this));
        }

        // Update fullscreen button
        this.fullscreenButton.innerHTML = '⛝';
        this.fullscreenButton.title = 'Exit Fullscreen (ESC)';

        // Prevent body scroll
        document.body.style.overflow = 'hidden';
    }

    private preventFullscreenExit(event: Event): void {
        // Prevent any events from causing fullscreen to exit
        event.stopPropagation();
        if (this.isFullscreen && event.type !== 'keydown') {
            // For non-keydown events, just stop propagation
            return;
        }
    }

    private exitFullscreen(): void {
        this.isFullscreen = false;

        // Restore original styles
        this.editorElement.style.cssText = this.originalStyles;

        // Reset editable area
        const editableElement = this.editorElement.querySelector('.ck-editor__editable') as HTMLElement;
        if (editableElement) {
            editableElement.style.cssText = '';
            editableElement.style.minHeight = '500px';
            editableElement.style.resize = 'both';
            editableElement.style.overflow = 'auto';

            // Remove event listeners
            editableElement.removeEventListener('focus', this.preventFullscreenExit.bind(this));
            editableElement.removeEventListener('blur', this.preventFullscreenExit.bind(this));
            editableElement.removeEventListener('input', this.preventFullscreenExit.bind(this));
            editableElement.removeEventListener('keydown', this.preventFullscreenExit.bind(this));
            editableElement.removeEventListener('keyup', this.preventFullscreenExit.bind(this));
        }

        // Update fullscreen button
        this.fullscreenButton.innerHTML = '⛶';
        this.fullscreenButton.title = 'Toggle Fullscreen (ESC to exit)';

        // Restore body scroll
        document.body.style.overflow = '';
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent): void {
        // Handle ESC for fullscreen exit
        if (event.key === 'Escape' && this.isFullscreen) {
            event.preventDefault();
            event.stopPropagation();
            this.exitFullscreen();
            return;
        }

        // Handle clipboard shortcuts when editor is focused
        const editableArea = this.editorElement?.querySelector('.ck-editor__editable') as HTMLElement;
        if (!editableArea || !editableArea.contains(event.target as Node)) {
            return;
        }

        const isCtrl = event.ctrlKey || event.metaKey;

        if (isCtrl) {
            switch (event.key.toLowerCase()) {
                case 'v':
                    if (event.shiftKey) {
                        // Ctrl+Shift+V - Paste as plain text
                        event.preventDefault();
                        event.stopPropagation();
                        this.executeClipboardAction('pastePlain');
                    }
                    // Let regular Ctrl+V pass through to default behavior
                    break;
                case 'x':
                    // Ctrl+X - Cut (let default behavior handle it, but log)
                    break;
                case 'c':
                    // Ctrl+C - Copy (let default behavior handle it, but log)
                    break;
            }
        }
    }
}
